'use client';

import { CheckIcon, ChevronDownIcon, Cross1Icon } from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const selectTriggerVariants = cva(
  'flex h-12 w-full items-center shadow-none justify-between text-sm placeholder:text-muted-foreground focus-within:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        outlined: ['bg-background border border-border', 'ea-input__outlined'],
        filled: ['bg-input', 'ea-input__filled'],
      },
      rounded: {
        default: 'rounded-lg',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants> & {
      hideCaret?: boolean;
      onClear?: () => void;
      isClearable?: boolean;
      clearableIconPossition?: 'left' | 'right';
      expandIcon?: React.ReactNode;
      clearIcon?: React.ReactNode;
      isLoading?: boolean;
      innerTriggerClassName?: string;
    }
>(
  (
    {
      className,
      innerTriggerClassName,
      children,
      hideCaret,
      onClear,
      isClearable,
      clearableIconPossition = 'left',
      expandIcon,
      clearIcon,
      isLoading,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        className={cn(
          selectTriggerVariants({
            ...props,
          }),
          'relative gap-2 overflow-hidden',
          'ea-select__trigger',
          isClearable && 'pl-2',
          className,
        )}
      >
        {isClearable && clearableIconPossition === 'left' && (
          <SelectPrimitive.Icon
            asChild
            onClick={e => {
              e.stopPropagation();
              onClear?.();
            }}
            className="z-1"
          >
            {clearIcon ? (
              clearIcon
            ) : (
              <Cross1Icon
                className={cn(
                  'h-4 w-4 cursor-pointer opacity-50 hover:opacity-100',
                  'ea-select__clear-icon',
                )}
              />
            )}
          </SelectPrimitive.Icon>
        )}

        <SelectPrimitive.Trigger
          ref={ref}
          className={cn(
            'flex h-12 w-full items-center justify-between px-3 py-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            innerTriggerClassName,
            'ea-select__trigger-inner',
          )}
          {...props}
        >
          {children}
          <div className={cn('flex items-center gap-2', 'ea-select__trigger-icons-spinner-expand')}>
            {isLoading && <Spinner className="ea-select__spinner" />}
            {!hideCaret && (
              <SelectPrimitive.Icon
                asChild
                className={cn('h-full cursor-pointer', 'ea-select__expand-icon')}
              >
                {expandIcon ? expandIcon : <ChevronDownIcon className="h-4 w-4 opacity-50" />}
              </SelectPrimitive.Icon>
            )}
          </div>
        </SelectPrimitive.Trigger>
        {isClearable && clearableIconPossition === 'right' && (
          <SelectPrimitive.Icon
            asChild
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onClear?.();
            }}
            className="absolute right-[38px] top-1/2 z-[1000] translate-y-[-50%]"
          >
            {clearIcon ? (
              clearIcon
            ) : (
              <Cross1Icon
                className={cn(
                  'h-4 w-4 cursor-pointer opacity-50 hover:opacity-100',
                  'ea-select__clear-icon',
                )}
                onClick={e => {
                  e.stopPropagation();
                  onClear?.();
                }}
              />
            )}
          </SelectPrimitive.Icon>
        )}
      </div>
    );
  },
);

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        'ea-select__content',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          'ea-select__viewport',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-secondary focus:text-secondary-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      'ea-select__item',
      className,
    )}
    {...props}
  >
    <span
      className={cn(
        'absolute right-2 flex h-3.5 w-3.5 items-center justify-center',
        'ea-select__item-content',
      )}
    >
      <SelectPrimitive.ItemIndicator className={cn('', 'ea-select__item-indicator')}>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', 'ea-select__separator', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
